// ============================================================
// components/stashcard/VendorReach.tsx
//
// THE primary contact pattern. One promoted "verb the human"
// tile + up to three peek-stat alternates. Auto-logs to History
// on tap. Keyboard accelerators (⌘C/T/W/E/M/S) wired up.
// ============================================================

import { useEffect, useCallback } from 'react';
import { Globe, Mail, MapPin, MessageSquare, Phone, Share2 } from 'lucide-react';
import { useReachState, useLogActivity, useStashCardDetailStore } from '../../store/stash-card-detail/hooks';
import type { ReachChannel, ReachTile } from '../../store/stash-card-detail/types';
import { cn } from '@/lib/utils';

const CHANNEL_ICON: Record<ReachChannel, React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>> = {
  call: Phone,
  text: MessageSquare,
  email: Mail,
  web: Globe,
  share: Share2,
  map: MapPin,
};

const KEY_TO_CHANNEL: Record<string, ReachChannel> = {
  c: 'call', t: 'text', e: 'email', w: 'web', s: 'share', m: 'map',
};

interface VendorReachProps {
  /** Distance from user to vendor in miles — drives the map tile peek-stat. */
  distanceMi?: number | null;
  /** Compact rendering — single row, no peek subtitles. */
  compact?: boolean;
  className?: string;
}

export function VendorReach({
  distanceMi = null,
  compact = false,
  className,
}: VendorReachProps): React.JSX.Element | null {
  const reach = useReachState({ distanceMi });
  const logActivity = useLogActivity();
  const card = useStashCardDetailStore(s => s.card);
  const currentUserId = useStashCardDetailStore(s => s.currentUserStasherId);

  const handleTileActivate = useCallback(
    (tile: ReachTile) => {
      if (!card || !tile.available) return;

      // Share is a special case — no activity log, use Web Share API.
      if (tile.channel === 'share') {
        const shareData = {
          title: `Stash: ${card.vendor.companyName}`,
          text: `Check out ${card.vendor.companyName} — stashed by friends.`,
          url: typeof window !== 'undefined' ? window.location.href : '',
        };
        // Log the share activity FIRST so the History timeline records it.
        logActivity({
          activityType: { share: {} },
          stashCardId: card.recordID,
          stasherId: currentUserId,
          title: 'Shared Card',
          detail: shareData.url,
        });
        if (typeof navigator !== 'undefined') {
          const nav = navigator as Navigator;
          if (typeof nav.share === 'function') {
            void nav.share(shareData).catch(() => undefined);
          } else if (nav.clipboard) {
            void nav.clipboard.writeText(shareData.url).catch(() => undefined);
          }
        }
        return;
      }

      // Log the activity to the store BEFORE following the href, so the
      // History timeline updates even if the user navigates away.
      if (tile.activityType) {
        logActivity({
          activityType: tile.activityType,
          stashCardId: card.recordID,
          stasherId: currentUserId,
          title: tile.verb ?? '',
          detail: '',
        });
      }

      // Follow the channel link. For tel/sms/mailto/http(s) the browser handles it.
      if (tile.href) {
        if (tile.channel === 'web' || tile.channel === 'map') {
          window.open(tile.href, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = tile.href;
        }
      }
    },
    [card, currentUserId, logActivity],
  );

  // Wire ⌘C / ⌘T / ⌘W / ⌘E / ⌘M / ⌘S accelerators
  useEffect(() => {
    if (!reach) return;
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const ch = KEY_TO_CHANNEL[e.key.toLowerCase()];
      if (!ch) return;
      const tile = reach.primary.channel === ch ? reach.primary : reach.alts.find(t => t.channel === ch);
      if (!tile?.available) return;
      e.preventDefault();
      handleTileActivate(tile);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [reach, handleTileActivate]);

  if (!reach) return null;

  return (
    <div
      role="group"
      aria-label="Reach this vendor"
      className={cn(
        'grid gap-1.5 rounded-3xl border border-border bg-muted p-1.5',
        compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-[minmax(0,1.5fr)_minmax(0,2fr)]',
        className,
      )}
    >
      <PrimaryTile tile={reach.primary} onActivate={handleTileActivate} />
      <div
        className={cn(
          'grid gap-1.5',
          reach.alts.length === 3 ? 'grid-cols-3' : reach.alts.length === 2 ? 'grid-cols-2' : 'grid-cols-1',
        )}
      >
        {reach.alts.map(tile => (
          <AltTile key={tile.channel} tile={tile} onActivate={handleTileActivate} />
        ))}
      </div>
    </div>
  );
}

// ───── Primary tile ─────

interface TileProps {
  tile: ReachTile;
  onActivate: (tile: ReachTile) => void;
}

function PrimaryTile({ tile, onActivate }: TileProps): React.JSX.Element {
  const Icon = CHANNEL_ICON[tile.channel];
  return (
    <button
      type="button"
      onClick={() => onActivate(tile)}
      disabled={!tile.available}
      aria-label={`${tile.verb} — ${tile.meta}`}
      className={cn(
        'relative flex items-center gap-3.5 overflow-hidden rounded-[calc(theme(borderRadius.3xl)-0.375rem)] px-4 py-3.5 text-left transition-all',
        'bg-foreground text-background',
        'hover:-translate-y-px hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
        'disabled:opacity-50 disabled:hover:translate-y-0',
      )}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'radial-gradient(120% 80% at 0% 0%, rgba(10,132,255,0.22), transparent 55%)',
        }}
      />
      <span className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--stash-blue)] text-background shadow-lg">
        <Icon size={22} strokeWidth={2} />
      </span>
      <span className="relative z-10 min-w-0 flex-1">
        <span className="block truncate text-base font-bold tracking-tight">
          {tile.verb}
        </span>
        <span className="mt-0.5 block truncate text-xs font-medium text-background/65">
          {tile.meta}
        </span>
      </span>
      <kbd className="relative z-10 hidden shrink-0 rounded-md bg-background/10 px-1.5 py-0.5 font-mono text-[11px] text-background/70 sm:inline-block">
        {tile.kbd}
      </kbd>
    </button>
  );
}

// ───── Alt tile ─────

function AltTile({ tile, onActivate }: TileProps): React.JSX.Element {
  const Icon = CHANNEL_ICON[tile.channel];
  return (
    <button
      type="button"
      onClick={() => onActivate(tile)}
      disabled={!tile.available}
      aria-label={`${tile.channel} — ${tile.sub ?? ''}`}
      className={cn(
        'group/alt relative flex flex-col gap-0.5 rounded-[calc(theme(borderRadius.3xl)-0.375rem)] border border-transparent bg-card px-3 py-2.5 text-left transition-all',
        'hover:-translate-y-px hover:border-[var(--stash-blue)]/40 hover:shadow-sm',
        'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
        'disabled:opacity-50 disabled:hover:translate-y-0',
      )}
    >
      <span className="flex items-center gap-1.5 text-[var(--stash-blue)]">
        <Icon size={15} strokeWidth={2} />
        <span className="text-sm font-semibold tracking-tight">{tile.channel}</span>
      </span>
      <span className="truncate text-[11px] font-medium text-muted-foreground">
        {tile.sub}
      </span>
    </button>
  );
}
